// =============================
// Testimonials Carousel - Design 1
// =============================

class TestimonialsCarousel {
    constructor(container) {
        this.container = container;
        this.track = container.querySelector('.carousel-track');
        this.cards = Array.from(this.track.children);
        this.prevBtn = container.querySelector('.carousel-nav.prev');
        this.nextBtn = container.querySelector('.carousel-nav.next');
        this.indicatorsContainer = container.querySelector('.carousel-indicators');
        
        this.currentIndex = 0;
        this.autoplayInterval = null;
        this.autoplayDelay = 5000;

        this.init();
    }

    init() {
        this.createIndicators();
        this.attachEventListeners();
        this.updateCarousel();
        this.startAutoplay();
    }

    createIndicators() {
        if (!this.indicatorsContainer) {
            console.warn('TestimonialsCarousel: .carousel-indicators element not found. Indicators will not be rendered.');
            return;
        }
        this.cards.forEach((_, index) => {
            const indicator = document.createElement('div');
            indicator.classList.add('indicator');
            if (index === 0) indicator.classList.add('active');
            indicator.addEventListener('click', () => this.goToSlide(index));
            this.indicatorsContainer.appendChild(indicator);
        });
        this.indicators = Array.from(this.indicatorsContainer.children);
    }

    attachEventListeners() {
        this.prevBtn.addEventListener('click', () => this.prev());
        this.nextBtn.addEventListener('click', () => this.next());

        // Pause autoplay on hover
        this.container.addEventListener('mouseenter', () => this.stopAutoplay());
        this.container.addEventListener('mouseleave', () => this.startAutoplay());

        // Keyboard navigation
        document.addEventListener('keydown', (e) => {
            if (!this.isInViewport()) return;
            if (e.key === 'ArrowLeft') this.prev();
            if (e.key === 'ArrowRight') this.next();
        });
    }

    updateCarousel() {
        const offset = -this.currentIndex * 100;
        this.track.style.transform = `translateX(${offset}%)`;

        this.cards.forEach((card, index) => {
            card.classList.toggle('active', index === this.currentIndex);
        });

        if (this.indicators && Array.isArray(this.indicators)) {
            this.indicators.forEach((indicator, index) => {
                indicator.classList.toggle('active', index === this.currentIndex);
            });
        }
    }

    goToSlide(index) {
        this.currentIndex = index;
        this.updateCarousel();
        this.resetAutoplay();
    }

    next() {
        this.currentIndex = (this.currentIndex + 1) % this.cards.length;
        this.updateCarousel();
        this.resetAutoplay();
    }

    prev() {
        this.currentIndex = (this.currentIndex - 1 + this.cards.length) % this.cards.length;
        this.updateCarousel();
        this.resetAutoplay();
    }

    startAutoplay() {
        this.autoplayInterval = setInterval(() => this.next(), this.autoplayDelay);
    }

    stopAutoplay() {
        if (this.autoplayInterval) {
            clearInterval(this.autoplayInterval);
            this.autoplayInterval = null;
        }
    }

    resetAutoplay() {
        this.stopAutoplay();
        this.startAutoplay();
    }

    isInViewport() {
        const rect = this.container.getBoundingClientRect();
        return (
            rect.top >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
        );
    }
}

// =============================
// Testimonials Carousel - Design 2
// =============================

class TestimonialsBubbleCarousel {
    constructor(container) {
        this.container = container;
        this.slidesContainer = container.querySelector('.carousel-slides');
        this.slides = Array.from(this.slidesContainer.children);
        this.prevBtn = container.querySelector('.prev-btn');
        this.nextBtn = container.querySelector('.next-btn');
        this.dotsContainer = container.querySelector('.progress-dots');
        
        this.currentIndex = 0;
        this.autoplayInterval = null;
        this.autoplayDelay = 6000;

        this.init();
    }

    init() {
        this.createDots();
        this.attachEventListeners();
        this.updateSlides();
        this.startAutoplay();
    }

    createDots() {
        this.slides.forEach((_, index) => {
            const dot = document.createElement('div');
            dot.classList.add('dot');
            if (index === 0) dot.classList.add('active');
            dot.addEventListener('click', () => this.goToSlide(index));
            this.dotsContainer.appendChild(dot);
        });
        this.dots = Array.from(this.dotsContainer.children);
    }

    attachEventListeners() {
        this.prevBtn.addEventListener('click', () => this.prev());
        this.nextBtn.addEventListener('click', () => this.next());

        this.container.addEventListener('mouseenter', () => this.stopAutoplay());
        this.container.addEventListener('mouseleave', () => this.startAutoplay());

        // Touch swipe support
        let touchStartX = 0;
        let touchEndX = 0;

        this.slidesContainer.addEventListener('touchstart', (e) => {
            touchStartX = e.changedTouches[0].screenX;
        });

        this.slidesContainer.addEventListener('touchend', (e) => {
            touchEndX = e.changedTouches[0].screenX;
            if (touchStartX - touchEndX > 50) this.next();
            if (touchEndX - touchStartX > 50) this.prev();
        });
    }

    updateSlides() {
        const offset = -this.currentIndex * 100;
        this.slidesContainer.style.transform = `translateX(${offset}%)`;

        this.slides.forEach((slide, index) => {
            slide.classList.toggle('active', index === this.currentIndex);
        });

        this.dots.forEach((dot, index) => {
            dot.classList.toggle('active', index === this.currentIndex);
        });
    }

    goToSlide(index) {
        this.currentIndex = index;
        this.updateSlides();
        this.resetAutoplay();
    }

    next() {
        this.currentIndex = (this.currentIndex + 1) % this.slides.length;
        this.updateSlides();
        this.resetAutoplay();
    }

    prev() {
        this.currentIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
        this.updateSlides();
        this.resetAutoplay();
    }

    startAutoplay() {
        // Prevent multiple intervals
        if (this.autoplayInterval) return;
        this.autoplayInterval = setInterval(() => this.next(), this.autoplayDelay);
    }

    stopAutoplay() {
        if (this.autoplayInterval) {
            clearInterval(this.autoplayInterval);
            this.autoplayInterval = null;
        }
    }

    resetAutoplay() {
        this.stopAutoplay();
        this.startAutoplay();
    }
}

// =============================
// Testimonials Carousel - Design 3
// =============================

class TestimonialsStackCarousel {
    constructor(container) {
        this.container = container;
        this.stackContainer = container.querySelector('.stack-container');
        this.cards = Array.from(this.stackContainer.querySelectorAll('.testimonial-stack-card'));
        this.prevBtn = container.querySelector('.prev-stack');
        this.nextBtn = container.querySelector('.next-stack');
        this.currentSpan = container.querySelector('.stack-counter .current');
        this.totalSpan = container.querySelector('.stack-counter .total');
        
        this.currentIndex = 0;
        this.autoplayInterval = null;
        this.autoplayDelay = 5500;

        this.init();
    }

    init() {
        this.totalSpan.textContent = this.cards.length;
        this.attachEventListeners();
        this.updateStack();
        this.startAutoplay();
    }

    attachEventListeners() {
        this.prevBtn.addEventListener('click', () => this.prev());
        this.nextBtn.addEventListener('click', () => this.next());

        this.container.addEventListener('mouseenter', () => this.stopAutoplay());
        this.container.addEventListener('mouseleave', () => this.startAutoplay());
    }

    updateStack() {
        this.currentSpan.textContent = this.currentIndex + 1;

        this.cards.forEach((card, index) => {
            card.classList.remove('active');
            
            // Calculate position relative to current index
            let position = index - this.currentIndex;
            
            // Wrap positions (cards that went behind come back to front)
            if (position < 0) {
                position = this.cards.length + position;
            }
            
            card.setAttribute('data-index', position);
            
            if (position === 0) {
                card.classList.add('active');
            }
        });
    }

    next() {
        this.currentIndex = (this.currentIndex + 1) % this.cards.length;
        this.updateStack();
        this.resetAutoplay();
    }

    prev() {
        this.currentIndex = (this.currentIndex - 1 + this.cards.length) % this.cards.length;
        this.updateStack();
        this.resetAutoplay();
    }

    startAutoplay() {
        this.autoplayInterval = setInterval(() => this.next(), this.autoplayDelay);
    }

    stopAutoplay() {
        if (this.autoplayInterval) {
            clearInterval(this.autoplayInterval);
            this.autoplayInterval = null;
        }
    }

    resetAutoplay() {
        this.stopAutoplay();
        this.startAutoplay();
    }
}

// =============================
// Testimonials Carousel - Design 4
// =============================

class TestimonialsThreeCardCarousel {
    constructor(container) {
        this.container = container;
        this.slidesContainer = container.querySelector('.carousel-slides');
        this.cards = Array.from(this.slidesContainer.querySelectorAll('.testimonial-card-4'));
        this.prevBtn = container.querySelector('.prev-btn-4');
        this.nextBtn = container.querySelector('.next-btn-4');
        this.dotsContainer = container.querySelector('.progress-dots');
        
        this.currentIndex = 0;
        this.autoplayInterval = null;
        this.autoplayDelay = 6000;
        this.isMobile = window.innerWidth <= 992;

        this.init();
    }

    init() {
        this.createDots();
        this.attachEventListeners();
        this.updateCards();
        this.startAutoplay();
        
        // Handle window resize
        window.addEventListener('resize', () => {
            const wasMobile = this.isMobile;
            this.isMobile = window.innerWidth <= 992;
            if (wasMobile !== this.isMobile) {
                this.updateCards();
            }
        });
    }

    createDots() {
        if (!this.dotsContainer) return;
        
        this.cards.forEach((_, index) => {
            const dot = document.createElement('div');
            dot.classList.add('dot');
            if (index === 0) dot.classList.add('active');
            dot.addEventListener('click', () => this.goToSlide(index));
            this.dotsContainer.appendChild(dot);
        });
        this.dots = Array.from(this.dotsContainer.children);
    }

    attachEventListeners() {
        if (this.prevBtn) {
            this.prevBtn.addEventListener('click', () => this.prev());
        }
        if (this.nextBtn) {
            this.nextBtn.addEventListener('click', () => this.next());
        }

        this.container.addEventListener('mouseenter', () => this.stopAutoplay());
        this.container.addEventListener('mouseleave', () => this.startAutoplay());

        // Click on side cards to navigate (desktop only)
        this.cards.forEach((card, index) => {
            card.addEventListener('click', () => {
                if (!this.isMobile && card.classList.contains('side-card')) {
                    this.goToSlide(index);
                }
            });
        });

        // Touch swipe support for mobile
        let touchStartX = 0;
        let touchEndX = 0;

        this.slidesContainer.addEventListener('touchstart', (e) => {
            touchStartX = e.changedTouches[0].screenX;
        });

        this.slidesContainer.addEventListener('touchend', (e) => {
            touchEndX = e.changedTouches[0].screenX;
            const swipeThreshold = 50;
            if (touchStartX - touchEndX > swipeThreshold) this.next();
            if (touchEndX - touchStartX > swipeThreshold) this.prev();
        });
    }

    updateCards() {
        if (this.isMobile) {
            // Mobile: single card view
            const offset = -this.currentIndex * 100;
            this.slidesContainer.style.transform = `translateX(${offset}%)`;

            this.cards.forEach((card, index) => {
                card.classList.remove('side-card', 'center-card', 'hidden-card', 'mobile-active');
                if (index === this.currentIndex) {
                    card.classList.add('mobile-active');
                }
            });
        } else {
            // Desktop: three cards view with center prominence
            this.slidesContainer.style.transform = 'translateX(0)';

            this.cards.forEach((card, index) => {
                card.classList.remove('side-card', 'center-card', 'hidden-card', 'mobile-active');
            });

            const prevIndex = (this.currentIndex - 1 + this.cards.length) % this.cards.length;
            const nextIndex = (this.currentIndex + 1) % this.cards.length;

            // Reorder DOM elements for proper display
            this.slidesContainer.innerHTML = '';
            this.cards.forEach(card => card.classList.add('hidden-card'));

            const prevCard = this.cards[prevIndex];
            const currentCard = this.cards[this.currentIndex];
            const nextCard = this.cards[nextIndex];

            prevCard.classList.remove('hidden-card');
            prevCard.classList.add('side-card');
            this.slidesContainer.appendChild(prevCard);

            currentCard.classList.remove('hidden-card');
            currentCard.classList.add('center-card');
            this.slidesContainer.appendChild(currentCard);

            nextCard.classList.remove('hidden-card');
            nextCard.classList.add('side-card');
            this.slidesContainer.appendChild(nextCard);

            // Add remaining hidden cards
            this.cards.forEach((card, index) => {
                if (index !== this.currentIndex && index !== prevIndex && index !== nextIndex) {
                    card.classList.add('hidden-card');
                    this.slidesContainer.appendChild(card);
                }
            });
        }

        // Update dots: always clear all, then set only the current as active
        if (this.dots && Array.isArray(this.dots)) {
            this.dots.forEach((dot, index) => {
                if (index === this.currentIndex) {
                    dot.classList.add('active');
                } else {
                    dot.classList.remove('active');
                }
            });
        }
    }

    goToSlide(index) {
        this.currentIndex = index;
        this.updateCards();
        this.resetAutoplay();
    }

    next() {
        this.currentIndex = (this.currentIndex + 1) % this.cards.length;
        this.updateCards();
        this.resetAutoplay();
    }

    prev() {
        this.currentIndex = (this.currentIndex - 1 + this.cards.length) % this.cards.length;
        this.updateCards();
        this.resetAutoplay();
    }

    startAutoplay() {
        this.autoplayInterval = setInterval(() => this.next(), this.autoplayDelay);
    }

    stopAutoplay() {
        if (this.autoplayInterval) {
            clearInterval(this.autoplayInterval);
            this.autoplayInterval = null;
        }
    }

    resetAutoplay() {
        this.stopAutoplay();
        this.startAutoplay();
    }
}

// =============================
// Initialize all carousels on page load
// =============================

document.addEventListener('DOMContentLoaded', () => {
    // Design 1
    const carousel1 = document.querySelector('#feature-testimonials .testimonials-carousel');
    if (carousel1) {
        new TestimonialsCarousel(carousel1);
    }

    // Design 2
    const carousel2 = document.querySelector('#feature-testimonials .testimonials-carousel-2');
    if (carousel2) {
        new TestimonialsBubbleCarousel(carousel2);
    }

    // Design 3
    const carousel3 = document.querySelector('#feature-testimonials3 .testimonials-stack');
    if (carousel3) {
        new TestimonialsStackCarousel(carousel3);
    }

    // Design 4
    const carousel4 = document.querySelector('#feature-testimonials4 .testimonials-carousel-4');
    if (carousel4) {
        new TestimonialsThreeCardCarousel(carousel4);
    }
});
